import { mainSiteUrl } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function ContentSections() {
  return (
    <>
      <WhatIsSection />
      <WhyUseSection />
      <ReviewSection />
      <ShippingSection />
      <CouponSection />
      <LegitSection />
      <ArticleSection />
    </>
  );
}

function WhatIsSection() {
  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
          What Is Sugargoo Spreadsheet?
        </h2>
        <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
          <p>
            <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> is a structured collection of product resources that helps shoppers discover trending items, compare products, and save time when shopping through Sugargoo. Instead of searching randomly across thousands of products, users can access organized categories, recommendations, and research-based lists curated by experienced shoppers.
          </p>
          <p>
            The concept behind a Sugargoo spreadsheet is simple yet powerful: imagine having a personal shopping assistant that has already done the hard work of finding the best products across dozens of categories. From streetwear staples to luxury accessories, each spreadsheet entry typically includes product links, quality ratings, price comparisons, and real user feedback — all in one easy-to-navigate format.
          </p>
        </div>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-background-200">
                <th className="text-left py-3 px-4 text-foreground-700 font-semibold">Method</th>
                <th className="text-left py-3 px-4 text-foreground-700 font-semibold">Search Speed</th>
                <th className="text-left py-3 px-4 text-foreground-700 font-semibold">Product Organization</th>
                <th className="text-left py-3 px-4 text-foreground-700 font-semibold">Beginner Friendly</th>
              </tr>
            </thead>
            <tbody className="text-foreground-600">
              <tr className="border-b border-background-100">
                <td className="py-3 px-4">Google Search</td>
                <td className="py-3 px-4">Slow</td>
                <td className="py-3 px-4">Low</td>
                <td className="py-3 px-4">Medium</td>
              </tr>
              <tr className="border-b border-background-100">
                <td className="py-3 px-4">Social Media Finds</td>
                <td className="py-3 px-4">Medium</td>
                <td className="py-3 px-4">Low</td>
                <td className="py-3 px-4">Medium</td>
              </tr>
              <tr className="border-b border-background-100">
                <td className="py-3 px-4 font-semibold text-primary-700">Sugargoo Spreadsheet</td>
                <td className="py-3 px-4 font-semibold text-accent-700">Fast</td>
                <td className="py-3 px-4 font-semibold text-accent-700">High</td>
                <td className="py-3 px-4 font-semibold text-accent-700">High</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function WhyUseSection() {
  const benefits = [
    { icon: 'ri-timer-flash-line', title: 'Save Time', desc: 'Find products without endless searching through unorganized listings and forum threads.' },
    { icon: 'ri-folder-line', title: 'Better Organization', desc: 'Products sorted by category, making it effortless to locate exactly what you need.' },
    { icon: 'ri-scales-3-line', title: 'Smart Shopping', desc: 'Compare products side by side before buying — avoid costly mistakes and regrets.' },
    { icon: 'ri-user-smile-line', title: 'Beginner Friendly', desc: 'Designed for first-time Sugargoo users with clear navigation and helpful guidance.' },
  ];

  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
          Why Use Sugargoo Spreadsheet?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {benefits.map((b, i) => (
            <div
              key={b.title}
              className="bg-white p-6 rounded-xl border border-background-200 hover:border-accent-200 transition-all duration-300 animate-fade-in-up"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                <i className={`${b.icon} text-lg`}></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">{b.title}</h3>
              <p className="text-foreground-500 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReviewSection() {
  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
          Sugargoo Review & Platform Analysis
        </h2>
        <p className="text-foreground-600 text-sm md:text-base mb-8 leading-relaxed">
          Based on extensive research and community feedback, here is our independent analysis of the Sugargoo platform. We evaluated key features that matter most to international shoppers looking for a reliable shopping agent.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-accent-50 rounded-xl p-6 border border-accent-100">
            <h3 className="font-heading font-semibold text-accent-800 mb-4 flex items-center gap-2">
              <i className="ri-thumb-up-line"></i> Pros
            </h3>
            <ul className="space-y-2 text-sm text-foreground-600">
              {['Comprehensive product sourcing across multiple platforms', 'Reliable warehouse service with quality inspection photos', 'International shipping to major destinations worldwide', 'User-friendly interface suitable for beginners'].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-secondary-50 rounded-xl p-6 border border-secondary-100">
            <h3 className="font-heading font-semibold text-secondary-700 mb-4 flex items-center gap-2">
              <i className="ri-information-line"></i> Considerations
            </h3>
            <ul className="space-y-2 text-sm text-foreground-600">
              {['Shipping costs vary depending on package weight and destination', 'Delivery time depends on the shipping method and destination country', 'Some items may have restrictions based on brand policies'].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <i className="ri-information-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-background-200">
                <th className="text-left py-3 px-4 text-foreground-700 font-semibold">Feature</th>
                <th className="text-left py-3 px-4 text-foreground-700 font-semibold">Sugargoo</th>
              </tr>
            </thead>
            <tbody className="text-foreground-600">
              {[
                ['Warehouse Service', 'Available'],
                ['QC Photos', 'Available'],
                ['International Shipping', 'Available'],
                ['Product Sourcing', 'Available'],
                ['Customer Support', 'Responsive'],
              ].map(([feature, value]) => (
                <tr key={feature} className="border-b border-background-100">
                  <td className="py-3 px-4">{feature}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1.5 text-accent-700 font-medium">
                      <i className="ri-checkbox-circle-fill text-accent-500"></i>
                      {value}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ShippingSection() {
  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
          Sugargoo Shipping Cost Guide
        </h2>
        <p className="text-foreground-600 text-sm md:text-base mb-8 leading-relaxed">
          Understanding Sugargoo shipping costs is essential for budgeting your international shopping. Several factors influence the final shipping price, and being aware of them helps you make smarter purchasing decisions with your Sugargoo spreadsheet finds.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { factor: 'Package Weight', impact: 'High', desc: 'Heavier packages cost significantly more to ship internationally.' },
            { factor: 'Destination', impact: 'High', desc: 'Shipping rates vary dramatically based on your country and region.' },
            { factor: 'Shipping Method', impact: 'Medium', desc: 'Express services cost more but deliver faster than standard options.' },
            { factor: 'Package Size', impact: 'Medium', desc: 'Volumetric weight can increase costs for bulky but lightweight items.' },
          ].map((item) => (
            <div key={item.factor} className="bg-white p-5 rounded-xl border border-background-200">
              <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wide">{item.factor}</span>
              <div className="mt-2 mb-1">
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  item.impact === 'High' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {item.impact} Impact
                </span>
              </div>
              <p className="text-foreground-500 text-sm mt-2">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CouponSection() {
  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
          Sugargoo Coupon & Discount Codes
        </h2>
        <p className="text-foreground-600 text-sm md:text-base mb-8 leading-relaxed">
          Savvy shoppers using a <strong className="text-foreground-900">Sugargoo spreadsheet</strong> know that combining organized product research with available coupons and discount codes maximizes savings. Here is what you need to know about finding and using Sugargoo promotions effectively.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: 'ri-coupon-line', title: 'Seasonal Promotions', desc: 'Major shopping holidays often bring the best discount opportunities. Keep an eye on holiday sales events throughout the year.' },
            { icon: 'ri-user-star-line', title: 'New User Discounts', desc: 'First-time Sugargoo users can often access welcome coupons and special introductory shipping rates.' },
            { icon: 'ri-refresh-line', title: 'Ongoing Deals', desc: 'Regular promotions and referral programs provide consistent ways to save on your Sugargoo spreadsheet purchases.' },
          ].map((item) => (
            <div key={item.title} className="bg-white p-5 rounded-xl border border-background-200 hover:border-primary-200 transition-colors">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                <i className={`${item.icon} text-lg`}></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">{item.title}</h3>
              <p className="text-foreground-500 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white font-medium text-sm rounded-full hover:bg-accent-600 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-price-tag-3-line"></i>
            Check Latest Deals
          </a>
        </div>
      </div>
    </section>
  );
}

function LegitSection() {
  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
          Is Sugargoo Legit?
        </h2>
        <p className="text-foreground-600 text-sm md:text-base mb-8 leading-relaxed">
          Yes, Sugargoo is an established shopping agent platform that has served thousands of international customers. The platform operates a legitimate warehouse system, provides quality checking processes, and maintains responsive customer support. When using a <strong className="text-foreground-900">Sugargoo spreadsheet</strong> to organize your purchases, you can shop with confidence knowing the platform has a proven track record.
        </p>

        <div className="space-y-4">
          {[
            { q: 'Does Sugargoo have a real warehouse?', a: 'Yes, Sugargoo operates physical warehouse facilities where your purchased items are stored, inspected, and prepared for international shipping. Quality check photos are provided for every item.' },
            { q: 'How does Sugargoo protect buyers?', a: 'Sugargoo offers quality inspection services, secure payment processing, and a structured dispute resolution system to protect buyer interests throughout the purchasing process.' },
            { q: 'Can I trust Sugargoo with expensive items?', a: 'Many users have successfully purchased high-value items through Sugargoo. The QC photo service and warehouse storage provide additional layers of security for expensive purchases.' },
            { q: 'What do real users say about Sugargoo?', a: 'Community feedback is largely positive, with users praising the organized interface, reliable warehouse service, and consistent quality inspection photos. The Sugargoo spreadsheet community continues to grow as more shoppers share their positive experiences.' },
          ].map((faq) => (
            <details key={faq.q} className="bg-white rounded-xl border border-background-200 group">
              <summary className="px-6 py-4 cursor-pointer font-medium text-foreground-800 text-sm flex items-center justify-between hover:text-foreground-950 transition-colors">
                {faq.q}
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-4 text-foreground-600 text-sm leading-relaxed">
                {faq.a}
              </div>
            </details>
          ))}
        </div>

        <p className="mt-8 text-xs text-foreground-400 italic text-center">
          Disclaimer: This website is an independent educational resource. We are not affiliated with Sugargoo. All trademarks belong to their respective owners.
        </p>
      </div>
    </section>
  );
}

function ArticleSection() {
  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-4xl mx-auto text-center">
        <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100/70 text-accent-700 text-xs font-semibold uppercase tracking-wide mb-4">
          In-Depth Guide
        </span>
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
          Ultimate Sugargoo Spreadsheet Guide 2026
        </h2>
        <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base mb-8">
          Dive deep into our comprehensive guide covering everything from finding the best Sugargoo spreadsheet resources to advanced shopping strategies that save you time and money.
        </p>
        <Link
          to="/article/sugargoo-spreadsheet-guide"
          className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 bg-primary-500 text-white font-semibold text-base rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer"
        >
          <i className="ri-book-open-line"></i>
          Read Full Guide
        </Link>
      </div>
    </section>
  );
}